/**
 * Who is asking.
 *
 * Report links are public: anyone holding the share URL can open one, and the
 * session only records the candidate, never the person reading about them. So
 * before either call to action goes anywhere we ask who they are, otherwise a
 * request reaches the team with nobody to reply to.
 *
 * Kept to four fields and one click. Name, work email and organisation are
 * required because they are what a reply needs; phone is optional.
 */
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icons";

export type ContactIntent = "interview" | "support";

export type ContactDetails = {
  name: string;
  email: string;
  organisation: string;
  phone: string;
};

/**
 * Mailbox providers that exist to be thrown away. Someone using a personal
 * address is fine and common, so those are not blocked; an address that stops
 * existing in ten minutes cannot receive our reply, so it is.
 */
const BURNER = /(mailinator|guerrillamail|10minutemail|tempmail|temp-mail|yopmail|throwaway|trashmail|sharklasers|dispostable|fakeinbox|getnada|maildrop)\./i;

const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

/** A form filled faster than a person can read it was not filled by a person. */
const MIN_FILL_MS = 2500;

const COPY: Record<ContactIntent, { title: string; sub: string; cta: string }> = {
  interview: {
    title: "Set up an interview",
    sub: "Tell us who you are and we will open this candidate for you and pass your request to our team.",
    cta: "Continue to the candidate",
  },
  support: {
    title: "Ask our team",
    sub: "Tell us where to reach you and we will come back with extra checks, references, or a human review of this result.",
    cta: "Send my request",
  },
};

export default function ContactDialog({
  intent, candidateName, onClose, onSubmit,
}: {
  intent: ContactIntent;
  candidateName: string;
  onClose: () => void;
  onSubmit: (d: ContactDetails, spam: { elapsedMs: number; honeypot: string }) => Promise<void>;
}) {
  const uid = useId();
  const [d, setD] = useState<ContactDetails>({ name: "", email: "", organisation: "", phone: "" });
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Partial<Record<keyof ContactDetails, string>>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const openedAt = useRef(Date.now());
  const first = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    first.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      // Focus must not wander to the page behind an open dialog.
      if (e.key === "Tab" && panel.current) {
        const f = panel.current.querySelectorAll<HTMLElement>("input:not(.hpf), button");
        if (!f.length) return;
        const [a, z] = [f[0], f[f.length - 1]];
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const set = (k: keyof ContactDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setD((p) => ({ ...p, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  function validate(): boolean {
    const e: Partial<Record<keyof ContactDetails, string>> = {};
    if (d.name.trim().length < 2) e.name = "Please tell us your name.";
    if (!EMAIL.test(d.email.trim())) e.email = "That does not look like an email address.";
    else if (BURNER.test(d.email.trim())) e.email = "Please use an address we can reply to.";
    if (d.organisation.trim().length < 2) e.organisation = "Please tell us where you work.";
    // Phone is optional, but a value that is there must be usable.
    if (d.phone.trim() && d.phone.replace(/\D/g, "").length < 7) e.phone = "That number looks too short.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    setFailed(null);
    try {
      await onSubmit(
        {
          name: d.name.trim(), email: d.email.trim().toLowerCase(),
          organisation: d.organisation.trim(), phone: d.phone.trim(),
        },
        { elapsedMs: Date.now() - openedAt.current, honeypot },
      );
    } catch {
      setFailed("We could not send that just now. Please try again.");
      setBusy(false);
    }
  }

  const c = COPY[intent];

  return createPortal(
    <div className="rpt rpt-dlg" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlg" ref={panel} role="dialog" aria-modal="true" aria-labelledby={`${uid}-t`}>
        <h3 id={`${uid}-t`}>
          <Icon id={intent === "interview" ? "cal" : "msg"} />{c.title}
        </h3>
        <p className="dsub">{c.sub}</p>

        <form onSubmit={submit} noValidate>
          <Field id={`${uid}-n`} label="Your name" value={d.name} onChange={set("name")}
            error={errors.name} inputRef={first} autoComplete="name" />
          <Field id={`${uid}-e`} label="Work email" value={d.email} onChange={set("email")}
            error={errors.email} type="email" autoComplete="email" />
          <Field id={`${uid}-o`} label="Company or organisation" value={d.organisation}
            onChange={set("organisation")} error={errors.organisation} autoComplete="organization" />
          <Field id={`${uid}-p`} label="Phone" optional value={d.phone} onChange={set("phone")}
            error={errors.phone} type="tel" autoComplete="tel" />

          {/* Left empty by people, filled in by bots that submit every field. */}
          <div className="hp" aria-hidden="true">
            <input className="hpf" tabIndex={-1} autoComplete="off"
              name="company_website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
          </div>

          {failed && <p className="err" role="alert">{failed}</p>}

          <div className="drow">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Sending" : <>{c.cta}<Icon id="chev" /></>}
            </button>
          </div>
        </form>

        <p className="note">
          We use these details only to reply to you about {candidateName}. No newsletter, no sharing.
        </p>
      </div>
    </div>,
    document.body,
  );
}

function Field({ id, label, optional, error, inputRef, ...rest }: any) {
  return (
    <div className="fld">
      <label htmlFor={id}>{label}{optional && <span className="opt">optional</span>}</label>
      <input id={id} ref={inputRef} aria-invalid={!!error}
        aria-describedby={error ? `${id}-e` : undefined} {...rest} />
      {error && <p className="err" id={`${id}-e`}>{error}</p>}
    </div>
  );
}

export { MIN_FILL_MS, BURNER, EMAIL };
